/**
 * @fileoverview User Management API service.
 * @description Reads the user directory (GET /users), one page at a time or every page at once,
 * its KPI summary (GET /users/summary) and table filter options (GET /users/filter-options, for
 * the DIRECTORY or MY_TEAM table); reads the caller's own team (GET /users/me/team, every page)
 * and its KPI summary (GET /users/me/team/summary); reads
 * one user's details (GET /users/{userId}) with the ETag the user commands need, updates a user
 * (PATCH /users/{userId}, sending that ETag as If-Match) and reads the Edit User form's choices
 * (GET /users/{userId}/edit-options). Also the lifecycle: the reasons to choose from (GET
 * /users/configuration), Deactivate User (POST /users/{userId}/deactivate) and Reactivate User
 * (POST /users/{userId}/reactivate), each sending the details' ETag as If-Match and the
 * Idempotency-Key the mutation hook made for the command (so a retry is a replay). The role,
 * manager and state choices the Add, Edit and Reactivate User dialogs offer are fetched by the shared
 * directory-options service.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 * @author Anshul Rokde
 */

import { ApiException } from '@/shared/api/api.exception'
import { httpClient } from '@/shared/api/http-client'
import { toPaginated } from '@/shared/api/pagination.utils'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'

import type {
  DeactivateUserRequest,
  DirectoryUser,
  ListUsersFilters,
  ListUsersParams,
  MyTeamSummary,
  OptionsData,
  ReactivatedUser,
  ReactivateUserRequest,
  ReportingManagerOption,
  RoleOption,
  StateFilterOption,
  TeamMember,
  UpdateUserRequest,
  UserDetail,
  UserFilterOptionKind,
  UserFilterOptionsContext,
  UserManagementConfiguration,
  UsersSummary,
  VersionedUserDetail,
} from '@/features/users/types/users.types'
import type { ResponseWithPageMeta } from '@/shared/api/pagination.utils'
import type { Paginated } from '@/shared/types/http.types'
import type { AxiosResponse } from 'axios'

// The backend's maximum page size.
const PAGE_LIMIT = 100
// A safety stop well above any real directory size, so a misreported total can't loop forever.
const MAX_PAGES = 50

/**
 * @description Reads every page of a paginated list. A full page means there may be more, and a
 * page `meta` that says more exist means the same, so a response without page meta (which the
 * pagination helpers fill in as a single page of exactly the items it carried) can't end the read
 * after a full first page. It stops at a short or empty page, and when a full page was the last one
 * the next read comes back empty. Running out of pages while the list still goes on is an error: a
 * list truncated without a signal is worse than a failed read.
 * @param {(page: number) => Promise<Paginated<T>>} fetchPage - Fetches one page of 100.
 * @returns {Promise<T[]>} Every item, in the backend's order.
 * @throws {ApiException} When the list is still going after MAX_PAGES pages.
 */
async function readAllPages<T>(fetchPage: (page: number) => Promise<Paginated<T>>): Promise<T[]> {
  const items: T[] = []

  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const { items: pageItems, meta } = await fetchPage(page)
    items.push(...pageItems)

    const mayHaveMore = pageItems.length >= PAGE_LIMIT || items.length < meta.total
    if (pageItems.length === 0 || !mayHaveMore) {
      return items
    }
  }

  throw new ApiException(
    ERROR_CODES.UNKNOWN_ERROR,
    'This list is too long to load in full. Please narrow it down and try again.',
  )
}

/**
 * @description Turns an options response (`{ kind, items }` with pagination `meta`) into a page.
 * @param {ResponseWithPageMeta<OptionsData<T>>} response - The options endpoint's response.
 * @returns {Paginated<T>} The page's choices and position.
 */
function toOptionsPage<T>(response: ResponseWithPageMeta<OptionsData<T>>): Paginated<T> {
  const { items } = response.data
  return {
    items,
    meta: response.pageMeta ?? { page: 1, limit: items.length, total: items.length },
  }
}

/**
 * @description Pairs a user-details response with its ETag. A response without an ETag is an
 * error: carrying on with an empty one would send an empty If-Match on the next command, which the
 * backend refuses as a missing precondition (428).
 * @param {AxiosResponse<T>} response - The GET, PATCH or POST /users/{userId}... response.
 * @returns {{ user: T, etag: string }} The user body and the version to send as the next If-Match.
 * @throws {ApiException} When the response carries no ETag header.
 */
function toVersionedUserDetail<T extends UserDetail>(
  response: AxiosResponse<T>,
): { user: T; etag: string } {
  const etag: unknown = response.headers.etag
  if (typeof etag !== 'string' || etag === '') {
    throw new ApiException(
      ERROR_CODES.UNKNOWN_ERROR,
      'The server did not say which version of this user it returned. Please try again.',
    )
  }
  return { user: response.data, etag }
}

/**
 * @description Fetches one page of the user directory.
 * @param {ListUsersParams} [params] - Filters, sort and page position.
 * @returns {Promise<Paginated<DirectoryUser>>} The page and its position.
 */
export async function getUsersPage(
  params: ListUsersParams = {},
): Promise<Paginated<DirectoryUser>> {
  const response = await httpClient.get<DirectoryUser[]>('/users', { params })
  return toPaginated(response)
}

/**
 * @description Tells whether anyone who is not deactivated reports directly to a user, i.e.
 * whether deactivating them would move anyone. Asks for one Active and one Pending report (one
 * row each) rather than reading every report just to get a yes or no.
 * @param {string} userId - The user.
 * @returns {Promise<boolean>} True when at least one direct report would move.
 */
export async function hasMovableDirectReports(userId: string): Promise<boolean> {
  const pages = await Promise.all(
    (['ACTIVE', 'PENDING'] as const).map((directoryStatus) =>
      getUsersPage({ reportingManagerUserId: userId, directoryStatus, limit: 1 }),
    ),
  )
  return pages.some((page) => page.items.length > 0)
}

/**
 * @description Fetches every user the caller may see, page by page.
 * @param {ListUsersFilters} [filters] - Filters and sort.
 * @returns {Promise<DirectoryUser[]>} Every matching user, in the backend's order.
 */
export function getAllUsers(filters: ListUsersFilters = {}): Promise<DirectoryUser[]> {
  return readAllPages((page) => getUsersPage({ ...filters, page, limit: PAGE_LIMIT }))
}

/**
 * @description Fetches one page of the caller's own team.
 * @param {ListUsersParams} [params] - Filters, sort and page position.
 * @returns {Promise<Paginated<TeamMember>>} The page and its position.
 */
export async function getMyTeamPage(params: ListUsersParams = {}): Promise<Paginated<TeamMember>> {
  const response = await httpClient.get<TeamMember[]>('/users/me/team', { params })
  return toPaginated(response)
}

/**
 * @description Fetches every member of the caller's team (themself included), page by page.
 * @param {ListUsersFilters} [filters] - Filters and sort.
 * @returns {Promise<TeamMember[]>} Every matching member, in the backend's order.
 */
export function getAllMyTeam(filters: ListUsersFilters = {}): Promise<TeamMember[]> {
  return readAllPages((page) => getMyTeamPage({ ...filters, page, limit: PAGE_LIMIT }))
}

/**
 * @description Fetches the team's KPI cards, which ignore the table's filters.
 * @returns {Promise<MyTeamSummary>} The counts by position and the banks the team covers.
 */
export async function getMyTeamSummary(): Promise<MyTeamSummary> {
  const { data } = await httpClient.get<MyTeamSummary>('/users/me/team/summary')
  return data
}

/**
 * @description Fetches the directory's KPI cards, which ignore the table's filters.
 * @returns {Promise<UsersSummary>} The counts by Sales position.
 */
export async function getUsersSummary(): Promise<UsersSummary> {
  const { data } = await httpClient.get<UsersSummary>('/users/summary')
  return data
}

/**
 * @description Fetches every role choice for a table's Role filter: the primary business
 * positions present in the caller's whole, unfiltered directory or team.
 * @param {UserFilterOptionsContext} [context] - The table (default DIRECTORY).
 * @returns {Promise<RoleOption[]>} Every choice, ordered by name.
 */
export function getRoleFilterOptions(
  context: UserFilterOptionsContext = 'DIRECTORY',
): Promise<RoleOption[]> {
  return getFilterOptions<RoleOption>('ROLE', context)
}

/**
 * @description Fetches every choice for a table's Reporting Manager filter: the managers that
 * members of the caller's whole, unfiltered directory or team report to.
 * @param {UserFilterOptionsContext} [context] - The table (default DIRECTORY).
 * @returns {Promise<ReportingManagerOption[]>} Every choice, ordered by name.
 */
export function getManagerFilterOptions(
  context: UserFilterOptionsContext = 'DIRECTORY',
): Promise<ReportingManagerOption[]> {
  return getFilterOptions<ReportingManagerOption>('REPORTING_MANAGER', context)
}

/**
 * @description Reads every page of GET /users/filter-options for a table.
 * @param {UserFilterOptionKind} kind - Which filter's choices.
 * @param {UserFilterOptionsContext} context - Which table (DIRECTORY or MY_TEAM).
 * @returns {Promise<T[]>} Every choice.
 */
function getFilterOptions<T>(
  kind: UserFilterOptionKind,
  context: UserFilterOptionsContext,
): Promise<T[]> {
  return readAllPages(async (page) =>
    toOptionsPage(
      await httpClient.get<OptionsData<T>>('/users/filter-options', {
        params: { context, kind, page, limit: PAGE_LIMIT },
      }),
    ),
  )
}

/**
 * @description Fetches every choice for the directory's State filter: the ACTIVE catalogue states,
 * then the synthetic All India option for members with no state.
 * @param {UserFilterOptionsContext} [context] - The table (default DIRECTORY).
 * @returns {Promise<StateFilterOption[]>} Every choice.
 */
export function getStateFilterOptions(
  context: UserFilterOptionsContext = 'DIRECTORY',
): Promise<StateFilterOption[]> {
  return getFilterOptions<StateFilterOption>('STATE', context)
}

/**
 * @description Fetches one user's details for View/Edit User, with the ETag the user commands need.
 * @param {string} userId - The user's id.
 * @returns {Promise<VersionedUserDetail>} The user and their current version.
 */
export async function getUserDetail(userId: string): Promise<VersionedUserDetail> {
  const response = await httpClient.get<UserDetail>(`/users/${encodeURIComponent(userId)}`)
  return toVersionedUserDetail(response)
}

/**
 * @description Updates a user. Needs the ETag from the details the edit started from: a stale one
 * is refused (412 / 3022) rather than overwriting someone else's change.
 * @param {string} userId - The user's id.
 * @param {UpdateUserRequest} payload - Only the fields that changed.
 * @param {string} etag - The version the edit started from, sent as If-Match.
 * @returns {Promise<VersionedUserDetail>} The updated user and their new version.
 */
export async function updateUser(
  userId: string,
  payload: UpdateUserRequest,
  etag: string,
): Promise<VersionedUserDetail> {
  const response = await httpClient.patch<UserDetail>(
    `/users/${encodeURIComponent(userId)}`,
    payload,
    { headers: { 'If-Match': etag } },
  )
  return toVersionedUserDetail(response)
}

/**
 * @description Fetches the primary Sales roles the caller may give this user, the current one
 * included.
 * @param {string} userId - The user being edited.
 * @returns {Promise<RoleOption[]>} Every role choice, ordered by name.
 */
export function getUserRoleEditOptions(userId: string): Promise<RoleOption[]> {
  return getUserEditOptions<RoleOption>(userId, { kind: 'ROLE' })
}

/**
 * @description Fetches the reporting managers this user may report to in the given role.
 * @param {string} userId - The user being edited.
 * @param {string} primaryRoleId - The proposed role (the current one to keep it).
 * @returns {Promise<ReportingManagerOption[]>} Every manager choice, ordered by name.
 */
export function getUserManagerEditOptions(
  userId: string,
  primaryRoleId: string,
): Promise<ReportingManagerOption[]> {
  return getUserEditOptions<ReportingManagerOption>(userId, {
    kind: 'REPORTING_MANAGER',
    primaryRoleId,
  })
}

/**
 * @description Reads every page of GET /users/{userId}/edit-options.
 * @param {string} userId - The user being edited.
 * @param {Record<string, string>} params - The kind, and the role for manager choices.
 * @returns {Promise<T[]>} Every choice.
 */
function getUserEditOptions<T>(userId: string, params: Record<string, string>): Promise<T[]> {
  return readAllPages(async (page) =>
    toOptionsPage(
      await httpClient.get<OptionsData<T>>(`/users/${encodeURIComponent(userId)}/edit-options`, {
        params: { ...params, page, limit: PAGE_LIMIT },
      }),
    ),
  )
}

/**
 * @description Fetches the lifecycle reasons and limits the Deactivate and Reactivate dialogs use.
 * @returns {Promise<UserManagementConfiguration>} The reasons (code and label) and note limit.
 */
export async function getUserManagementConfiguration(): Promise<UserManagementConfiguration> {
  const { data } = await httpClient.get<UserManagementConfiguration>('/users/configuration')
  return data
}

/**
 * @description Deactivates a user: they are signed out everywhere, and everyone reporting to them
 * moves up to the nearest eligible manager. Needs the ETag from the details the dialog showed: a
 * stale one is refused (412 / 3022) rather than acting on a user who has since changed. Refused
 * while the user owns an accepted prospect (409 / 3029).
 * @param {string} userId - The user's id.
 * @param {DeactivateUserRequest} payload - The reason, and a note for OTHER.
 * @param {string} etag - The version the dialog was shown, sent as If-Match.
 * @param {string} idempotencyKey - The key for this command, reused when it is sent again.
 * @returns {Promise<VersionedUserDetail>} The deactivated user and their new version.
 */
export async function deactivateUser(
  userId: string,
  payload: DeactivateUserRequest,
  etag: string,
  idempotencyKey: string,
): Promise<VersionedUserDetail> {
  const response = await httpClient.post<UserDetail>(
    `/users/${encodeURIComponent(userId)}/deactivate`,
    payload,
    { headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey } },
  )
  return toVersionedUserDetail(response)
}

/**
 * @description Reactivates a deactivated user with the role and reporting manager they come back
 * with, and queues their welcome-back email (or a fresh invitation, if they never finished
 * activating). Needs the ETag from the details the dialog showed (a stale one is 412 / 3022).
 * @param {string} userId - The user's id.
 * @param {ReactivateUserRequest} payload - The role, manager and reason.
 * @param {string} etag - The version the dialog was shown, sent as If-Match.
 * @param {string} idempotencyKey - The key for this command, reused when it is sent again.
 * @returns {Promise<ReactivatedUser>} The user as they are now, their version, and how they came
 * back.
 */
export async function reactivateUser(
  userId: string,
  payload: ReactivateUserRequest,
  etag: string,
  idempotencyKey: string,
): Promise<ReactivatedUser> {
  const response = await httpClient.post<
    UserDetail & Pick<ReactivatedUser, 'reactivationMode' | 'deliveryStatus'>
  >(`/users/${encodeURIComponent(userId)}/reactivate`, payload, {
    headers: { 'If-Match': etag, 'Idempotency-Key': idempotencyKey },
  })
  const { user: body, etag: version } = toVersionedUserDetail(response)
  const { reactivationMode, deliveryStatus, ...user } = body
  return { user, etag: version, reactivationMode, deliveryStatus }
}
